import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "helpers/generalHelper";
import { extractRowsWithErrors } from "helpers/importCellErrors";
import {
  SCREENSHOT_IMPORT_STATUS,
  TRADE_CAPTURE_OUTPUT,
  TRADE_CAPTURE_SOURCE,
} from "constants/tradeCapture";
import {
  LEGACY_TRADE_CAPTURE_LAST_SESSION_KEY,
  appendTradeCaptureCreateFields,
  buildAddScreenshotFormData,
  cancelTradeCaptureSessionInBackground,
  ensureActionedAtTimeOnRows,
  getScreenshotFileError,
  getScreenshotImportErrorMessage,
  isErrorResponse,
  isOtherSurfaceOutput,
  keepLadderRows,
  loadTradeCaptureImportPrefs,
  formatProductContractStyleLabel,
  normalizeProductContractStyleIdValue,
  parseOtherSurfaceUnderlying,
  saveTradeCaptureImportPrefs,
  withOtherSurfaceUnderlying,
} from "helpers/tradeCaptureHelper";
import {
  addSessionScreenshot,
  createSession,
  fillOtherSurface,
  previewOtherSurface,
  validateSession,
  importSession,
} from "api/tradeCaptureApi";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";

export const TRADE_CAPTURE_DESTINATION = {
  PRICER: "pricer",
  AUDIT_TRAIL: "audit_trail",
};

const handleApiError = (res, { screenshot = false } = {}) => {
  if (!res) {
    toast("Network error — please check your connection", "error");
    return;
  }
  const status = res.status;
  const message = getScreenshotImportErrorMessage(res);
  if (status === 502) {
    toast(
      message ||
        (screenshot
          ? "Reading the screenshot failed. Please try again."
          : "AI provider failed. Please try again."),
      "error"
    );
  } else if (status === 429) {
    toast(
      message ||
        "Too many screenshot uploads. Please wait a minute and try again.",
      "warning"
    );
  } else if (status === 422) {
    toast(message || "Validation error", "warning");
  } else if (status === 404) {
    toast(message || "Session not found", "error");
  } else if (message) {
    toast(message, "error");
  }
};

/**
 * `output` picks the flow. OPTIONS (default) is Option Capture: validate →
 * import to Pricer / Audit Trail. OTHER_SURFACE reads an option chain for one
 * tenor of the Other surface: edit rows + UL → preview → approve (fill) or
 * dismiss. Upload, add screenshot, cancel and error handling are shared.
 */
export default function useTradeCaptureModalController({
  open,
  onClose,
  refetchOptionRow,
  output = TRADE_CAPTURE_OUTPUT.OPTIONS,
  onOtherSurfaceFilled,
  onSurfaceLocked,
}) {
  const isOtherSurface = isOtherSurfaceOutput(output);
  const normalizeRows = isOtherSurface
    ? keepLadderRows
    : ensureActionedAtTimeOnRows;

  const [session, setSession] = useState(null);
  const [source, setSource] = useState(TRADE_CAPTURE_SOURCE.ICS_BLOTTER);
  const [loading, setLoading] = useState(false);
  const [addingScreenshot, setAddingScreenshot] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [validationStatusCode, setValidationStatusCode] = useState(null);

  const [importDestination, setImportDestination] = useState(null);
  const [importDate, setImportDate] = useState(null);

  // When backend returns 422 with vol_calculation_failed=true on a Pricer
  // import, we hold the message + failed option numbers so the drawer can
  // render a confirmation dialog offering to retry with skip_vol_calculation.
  const [volCalcFailedInfo, setVolCalcFailedInfo] = useState(null);

  // Other surface fill: the UL the fill writes (editable above the grid),
  // notices from add-screenshot merges, and the preview awaiting approval.
  const [underlying, setUnderlying] = useState("");
  const [notices, setNotices] = useState([]);
  const [otherSurfacePreview, setOtherSurfacePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [filling, setFilling] = useState(false);
  const [otherSurfaceProductId, setOtherSurfaceProductId] = useState(null);
  const [otherSurfaceProductStyle, setOtherSurfaceProductStyle] = useState(null);
  const underlyingEditedRef = useRef(false);
  const otherSurfaceProductEditedRef = useRef(false);
  const underlyingRef = useRef("");
  underlyingRef.current = underlying;
  const otherSurfaceProductIdRef = useRef(null);
  otherSurfaceProductIdRef.current = otherSurfaceProductId;

  const queryClient = useQueryClient();
  const sessionRef = useRef(null);
  sessionRef.current = session;
  // Bumped on close so a 30–60s OCR that returns after the popup is gone
  // cannot reopen the grid.
  const requestEpoch = useRef(0);
  const addingRef = useRef(false);

  // Holds the latest table_data the user is editing locally. Edits no longer
  // round-trip to the server; we send this on validate/import.
  const tableDataRef = useRef([]);

  const isBusy =
    loading || validating || addingScreenshot || previewing || filling;

  const resetState = useCallback(() => {
    requestEpoch.current += 1;
    addingRef.current = false;
    setSession(null);
    setSource(TRADE_CAPTURE_SOURCE.ICS_BLOTTER);
    setLoading(false);
    setAddingScreenshot(false);
    setValidating(false);
    setImporting(false);
    setValidationErrors(null);
    setValidationStatusCode(null);
    setImportDestination(null);
    setImportDate(null);
    setVolCalcFailedInfo(null);
    setUnderlying("");
    setNotices([]);
    setOtherSurfacePreview(null);
    setPreviewing(false);
    setFilling(false);
    setOtherSurfaceProductId(null);
    setOtherSurfaceProductStyle(null);
    underlyingEditedRef.current = false;
    otherSurfaceProductEditedRef.current = false;
    tableDataRef.current = [];
  }, []);

  const finalizeClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Clear legacy resume key on open.
  useEffect(() => {
    if (!open) return;
    try {
      localStorage.removeItem(LEGACY_TRADE_CAPTURE_LAST_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, [open]);

  // Parent may set `open` false without requestClose (e.g. PCS tab change).
  useEffect(() => {
    if (open) return;
    const s = sessionRef.current;
    if (s?.id && s.status !== "imported") {
      cancelTradeCaptureSessionInBackground(s.id);
    }
    resetState();
  }, [open, resetState]);

  const syncOtherSurfaceProductFromSession = useCallback((sessionData) => {
    if (!sessionData || otherSurfaceProductEditedRef.current) return;
    const id =
      normalizeProductContractStyleIdValue(
        sessionData.product_contract_style_id
      ) ??
      normalizeProductContractStyleIdValue(
        sessionData.product_contract_style?.id
      );
    setOtherSurfaceProductId(id);
    const style = sessionData.product_contract_style;
    if (style && typeof style === "object") {
      setOtherSurfaceProductStyle(style);
    } else if (id != null) {
      setOtherSurfaceProductStyle((prev) =>
        prev?.id === id ? prev : { id, commodity: "", contract_style: "" }
      );
    }
  }, []);

  // Best-effort: cancel server session on refresh/close tab. Once the session
  // has any imported destination (server flips status to "imported") there is
  // nothing to cancel server-side.
  useEffect(() => {
    if (!session?.id || session?.status === "imported") return;
    const handlePageHide = () =>
      cancelTradeCaptureSessionInBackground(session.id);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
  }, [session?.id, session?.status]);

  const hydrateImportUiFromStorage = useCallback(() => {
    const p = loadTradeCaptureImportPrefs();
    if (!p) return;
    if (
      p.destination === TRADE_CAPTURE_DESTINATION.PRICER ||
      p.destination === TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL
    ) {
      setImportDestination(p.destination);
    } else if (p.importToPricer === true) {
      // Backward compat with the old two-checkbox prefs shape.
      setImportDestination(TRADE_CAPTURE_DESTINATION.PRICER);
    } else if (p.pushToAuditTrail === true) {
      setImportDestination(TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL);
    }
    if (p.actionDate && moment(p.actionDate, "YYYY-MM-DD", true).isValid()) {
      setImportDate(moment(p.actionDate, "YYYY-MM-DD"));
    }
  }, []);

  useEffect(() => {
    if (!session?.id) return;
    saveTradeCaptureImportPrefs({
      destination: importDestination,
      actionDate: importDate?.format?.("YYYY-MM-DD") ?? null,
    });
  }, [session?.id, importDestination, importDate]);

  useEffect(() => {
    if (
      importDestination !== TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL ||
      !session
    ) {
      return;
    }
    setImportDate((d) => d || moment());
  }, [importDestination, session]);

  const requestClose = useCallback(() => {
    const s = sessionRef.current;
    if (!s || s.status === "imported") {
      finalizeClose();
      return;
    }
    const sessionId = s.id;
    finalizeClose();
    cancelTradeCaptureSessionInBackground(sessionId);
  }, [finalizeClose]);

  const clearValidation = useCallback(() => {
    setValidationErrors(null);
    setValidationStatusCode(null);
  }, []);

  // Keep the editable-table ref in sync with the server session so that a
  // subsequent validate/import (without further edits) sends the latest data.
  useEffect(() => {
    tableDataRef.current = normalizeRows(
      Array.isArray(session?.table_data) ? session.table_data : []
    );
  }, [normalizeRows, session?.table_data]);

  // The server's UL wins until the user edits it; a later screenshot keeps
  // the user's value.
  useEffect(() => {
    if (!isOtherSurface || underlyingEditedRef.current) return;
    const serverUl = session?.underlying;
    setUnderlying(serverUl == null ? "" : String(serverUl));
  }, [isOtherSurface, session?.underlying]);

  const applyValidationFailure = useCallback((res) => {
    if (res?.status === 422 && res?.data?.data) {
      setSession(res.data.data);
      // Canonical: rows_with_errors. Legacy alias: validation_errors.
      setValidationErrors(extractRowsWithErrors(res));
      setValidationStatusCode(422);
      toast(res.data.message, "warning");
      return true;
    }
    return false;
  }, []);

  /**
   * Ladder flows can queue several screenshots of one tenor on the upload
   * screen. The first creates the session; each extra one is then added in
   * order. A screenshot the server rejects (another tenor or product, nothing
   * to read) is reported by file name and skipped; the rest still load.
   */
  const addQueuedScreenshots = useCallback(
    async (firstSession, extraFiles, epoch) => {
      let current = firstSession;
      const skipped = [];
      const notices = [];
      for (const file of extraFiles) {
        const res = await addSessionScreenshot(
          current.id,
          buildAddScreenshotFormData(
            file,
            normalizeRows(Array.isArray(current.table_data) ? current.table_data : [])
          )
        );
        if (epoch !== requestEpoch.current) return null;
        if (isErrorResponse(res)) {
          const reason =
            getScreenshotImportErrorMessage(res) ||
            "it could not be read. Please try again.";
          skipped.push(`${file.name}: ${reason}`);
          continue;
        }
        const { notices: nextNotices, ...next } = res.data.data || {};
        current = next;
        // Each screenshot can carry its own notes (strike labels corrected from
        // the settles, a different UL), so keep them all.
        if (Array.isArray(nextNotices)) notices.push(...nextNotices);
      }
      return { session: current, skipped, notices };
    },
    [normalizeRows]
  );

  const handleUpload = useCallback(
    async (formData, extraFiles = []) => {
      const epoch = requestEpoch.current;
      setLoading(true);
      appendTradeCaptureCreateFields(formData, {
        source,
        output,
      });
      // Rows can come from the queued screenshots too (Other Call/Put picks
      // from the whole ladder), so the first one may have none on its own.
      if (extraFiles.length) formData.append("more_screenshots", "1");
      try {
        const res = await createSession(formData);
        if (epoch !== requestEpoch.current) return;
        if (isErrorResponse(res)) {
          handleApiError(res, { screenshot: true });
          return;
        }
        const { notices: createNotices, ...createdSession } = res.data.data || {};
        let created = createdSession;
        let readNotices = Array.isArray(createNotices) ? createNotices : [];
        if (extraFiles.length) {
          const result = await addQueuedScreenshots(created, extraFiles, epoch);
          if (!result) return;
          created = result.session;
          readNotices = [...readNotices, ...result.notices];
          if (result.skipped.length) {
            toast(
              `Not loaded — ${result.skipped.join(" | ")}`,
              "warning"
            );
          }
        }
        setSession(created);
        setNotices(readNotices);
        clearValidation();
        if (isOtherSurface) {
          syncOtherSurfaceProductFromSession(created);
          return;
        }
        hydrateImportUiFromStorage();
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER,
          action: ACTIONS.CLICK,
          component: COMPONENT_NAMES.BUTTON,
          eventName: EVENT_NAMES.PRICER_IMPORT_TRADES_UPLOAD_SUCCESS,
          extraParams: {
            session_id: res.data.data?.id,
            source: res.data.data?.source || source || undefined,
          },
        });
      } finally {
        if (epoch === requestEpoch.current) setLoading(false);
      }
    },
    [
      addQueuedScreenshots,
      clearValidation,
      hydrateImportUiFromStorage,
      isOtherSurface,
      output,
      source,
      syncOtherSurfaceProductFromSession,
    ]
  );

  const handleAddScreenshot = useCallback(
    async (file) => {
      if (!sessionRef.current?.id || addingRef.current) return;
      const fileError = getScreenshotFileError(file);
      if (fileError) {
        toast(fileError, "error");
        return;
      }
      const epoch = requestEpoch.current;
      const sessionId = sessionRef.current.id;
      addingRef.current = true;
      setAddingScreenshot(true);
      try {
        const res = await addSessionScreenshot(
          sessionId,
          buildAddScreenshotFormData(file, tableDataRef.current)
        );
        if (epoch !== requestEpoch.current) return;
        if (isErrorResponse(res)) {
          // The session belongs to someone else. Closing avoids editing a
          // grid the next add or import cannot save.
          if (res?.status === 404) {
            toast(getScreenshotImportErrorMessage(res) || "Session not found", "error");
            finalizeClose();
            return;
          }
          handleApiError(res, { screenshot: true });
          return;
        }
        const { notices: nextNotices, ...next } = res.data.data || {};
        // Add puts the session back to draft. Import stays closed until
        // validate runs again, even if the payload still says is_validated.
        setSession(
          next?.status === SCREENSHOT_IMPORT_STATUS.DRAFT
            ? { ...next, is_validated: false }
            : next
        );
        syncOtherSurfaceProductFromSession(next);
        clearValidation();
        // The grid changed, so any preview is stale.
        setOtherSurfacePreview(null);
        setNotices(Array.isArray(nextNotices) ? nextNotices : []);
      } finally {
        // A close (or a 404 close) bumps the epoch and already cleared this
        // flag. Don't let a late finally unlock a newer add.
        if (epoch === requestEpoch.current) {
          addingRef.current = false;
          setAddingScreenshot(false);
        }
      }
    },
    [clearValidation, finalizeClose, syncOtherSurfaceProductFromSession]
  );

  const handleTableUpdate = useCallback(
    (newTableData) => {
      tableDataRef.current = normalizeRows(
        Array.isArray(newTableData) ? newTableData : []
      );
      // Edits invalidate the previous server-side validation result.
      clearValidation();
      setOtherSurfacePreview(null);
      setSession((prev) =>
        prev?.is_validated ? { ...prev, is_validated: false } : prev
      );
    },
    [clearValidation, normalizeRows]
  );

  const handleUnderlyingChange = useCallback(
    (value) => {
      underlyingEditedRef.current = true;
      setUnderlying(value);
      clearValidation();
      setOtherSurfacePreview(null);
    },
    [clearValidation]
  );

  const handleOtherSurfaceProductChange = useCallback(
    (nextId, nextStyle) => {
      otherSurfaceProductEditedRef.current = true;
      setOtherSurfaceProductId(nextId);
      if (nextStyle && typeof nextStyle === "object") {
        setOtherSurfaceProductStyle(nextStyle);
      }
      clearValidation();
      setOtherSurfacePreview(null);
    },
    [clearValidation]
  );

  /** Grid rows with C/P and premium following the current UL. */
  const otherSurfacePayload = useCallback(() => {
    const ul = parseOtherSurfaceUnderlying(underlyingRef.current);
    const payload = {
      underlying: ul,
      table_data: withOtherSurfaceUnderlying(tableDataRef.current, ul),
    };
    const pcsId = otherSurfaceProductIdRef.current;
    if (pcsId != null) {
      payload.product_contract_style_id = pcsId;
    }
    return payload;
  }, []);

  const handlePreview = useCallback(async () => {
    if (!sessionRef.current?.id) return;
    const payload = otherSurfacePayload();
    if (payload.underlying == null) {
      toast("Enter a UL greater than 0 before previewing.", "warning");
      return;
    }
    const epoch = requestEpoch.current;
    setPreviewing(true);
    clearValidation();
    try {
      const res = await previewOtherSurface(sessionRef.current.id, payload);
      if (epoch !== requestEpoch.current) return;
      if (isErrorResponse(res)) {
        setOtherSurfacePreview(null);
        if (!applyValidationFailure(res)) handleApiError(res);
        return;
      }
      setOtherSurfacePreview(res.data.data);
    } finally {
      if (epoch === requestEpoch.current) setPreviewing(false);
    }
  }, [applyValidationFailure, clearValidation, otherSurfacePayload]);

  const dismissOtherSurfacePreview = useCallback(() => {
    setOtherSurfacePreview(null);
  }, []);

  const handleFill = useCallback(async () => {
    const preview = otherSurfacePreview;
    if (!sessionRef.current?.id || !preview?.surface_version) return;
    const epoch = requestEpoch.current;
    setFilling(true);
    try {
      const res = await fillOtherSurface(sessionRef.current.id, {
        ...otherSurfacePayload(),
        surface_version: preview.surface_version,
      });
      if (epoch !== requestEpoch.current) return;
      if (isErrorResponse(res)) {
        const message = getScreenshotImportErrorMessage(res);
        if (res?.status === 409) {
          // Someone changed the tenor after this preview: show what it
          // would do now instead of writing over their change.
          toast(
            message ||
              "The Other surface changed since the preview. Showing the updated preview.",
            "warning"
          );
          setOtherSurfacePreview(null);
          setFilling(false);
          handlePreview();
          return;
        }
        if (res?.status === 423) {
          // Same payload as other Other surface edits; the page owns the
          // lock / take-over dialog. The preview stays so the user can retry.
          if (onSurfaceLocked) {
            onSurfaceLocked(res.data);
          } else {
            toast(message || "The Other surface is locked by another user", "warning");
          }
          return;
        }
        if (res?.status === 422) setOtherSurfacePreview(null);
        if (!applyValidationFailure(res)) handleApiError(res);
        return;
      }
      const {
        other_surface_rows: otherSurfaceRows,
        other_surface_graph: otherSurfaceGraph,
        failed_strikes: failedStrikes,
        ...filledSession
      } = res.data.data || {};
      const hasFailedStrikes =
        Array.isArray(failedStrikes) && failedStrikes.length > 0;
      toast(res.data.message, hasFailedStrikes ? "warning" : "success");
      onOtherSurfaceFilled?.({
        tenor: preview.tenor,
        productContractStyleId: otherSurfaceProductIdRef.current,
        rows: Array.isArray(otherSurfaceRows) ? otherSurfaceRows : [],
        otherSurfaceGraph: otherSurfaceGraph ?? null,
        failedStrikes: hasFailedStrikes ? failedStrikes : [],
        session: filledSession,
      });
      // Nothing is left to do in the popup once the surface is written.
      finalizeClose();
    } finally {
      if (epoch === requestEpoch.current) setFilling(false);
    }
  }, [
    applyValidationFailure,
    finalizeClose,
    handlePreview,
    onOtherSurfaceFilled,
    onSurfaceLocked,
    otherSurfacePayload,
    otherSurfacePreview,
  ]);

  const handleApprove = useCallback(async () => {
    if (!sessionRef.current?.id) return;
    setValidating(true);
    setValidationErrors(null);
    try {
      const res = await validateSession(
        sessionRef.current.id,
        tableDataRef.current
      );
      if (isErrorResponse(res)) {
        setValidationStatusCode(res?.status ?? null);
        if (!applyValidationFailure(res)) {
          handleApiError(res);
        }
        return;
      }
      setSession(res.data.data);
      setValidationStatusCode(res?.status ?? 200);
      toast(res.data.message, "success");
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_IMPORT_TRADES_VALIDATE_SUCCESS,
        extraParams: {
          session_id: res.data.data?.id,
          source: res.data.data?.source || source || undefined,
        },
      });
    } finally {
      setValidating(false);
    }
  }, [applyValidationFailure, source]);

  const requestImport = useCallback(
    async ({ skipVolCalculation = false } = {}) => {
      if (!sessionRef.current?.id) return;
      if (!importDestination) {
        toast("Please select an import destination", "error");
        return;
      }
      const sentToPricer =
        importDestination === TRADE_CAPTURE_DESTINATION.PRICER;
      const sentToAuditTrail =
        importDestination === TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL;
      if (sentToAuditTrail && !importDate) {
        toast("Please select a date when pushing to audit trail", "warning");
        return;
      }
      setImporting(true);
      try {
        const payload = {
          import_to_pricer: sentToPricer,
          import_to_audit_trail: sentToAuditTrail,
          table_data: tableDataRef.current,
        };
        if (sentToAuditTrail && importDate) {
          payload.action_date = importDate.format("YYYY-MM-DD");
        }
        if (skipVolCalculation) {
          payload.skip_vol_calculation = true;
        }
        const res = await importSession(sessionRef.current.id, payload);
        if (isErrorResponse(res)) {
          // Pricer-only branch: backend asks the user whether to import with
          // vol=0 for the legs whose premium fell outside the bisection range.
          // Audit trail failures still come through as plain validation errors.
          const isVolCalcFailed =
            res?.status === 422 &&
            res?.data?.data?.vol_calculation_failed === true &&
            sentToPricer &&
            !skipVolCalculation;
          if (isVolCalcFailed) {
            setVolCalcFailedInfo({
              message: res.data.message || "",
              failedOptions: Array.isArray(res.data.data.failed_options)
                ? res.data.data.failed_options
                : [],
            });
            return;
          }
          setVolCalcFailedInfo(null);
          if (!applyValidationFailure(res)) {
            handleApiError(res);
          }
          return;
        }
        setSession(res.data.data);
        setVolCalcFailedInfo(null);
        toast(res.data.message, "success");
        if (sentToPricer) {
          if (refetchOptionRow) {
            refetchOptionRow();
          } else {
            queryClient.invalidateQueries(["option-rows"]);
          }
        }
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER,
          action: ACTIONS.CLICK,
          component: COMPONENT_NAMES.BUTTON,
          eventName: EVENT_NAMES.PRICER_IMPORT_TRADES_IMPORT_SUCCESS,
          extraParams: {
            import_to_pricer: !!sentToPricer,
            import_to_audit_trail: !!sentToAuditTrail,
            skip_vol_calculation: !!skipVolCalculation,
            session_id: res.data.data?.id,
            source: res.data.data?.source || source || undefined,
          },
        });
      } finally {
        setImporting(false);
      }
    },
    [
      applyValidationFailure,
      importDate,
      importDestination,
      queryClient,
      refetchOptionRow,
      source,
    ]
  );

  const handleImport = useCallback(() => requestImport(), [requestImport]);

  const confirmImportSkippingVol = useCallback(
    () => requestImport({ skipVolCalculation: true }),
    [requestImport]
  );

  const dismissVolCalcFailed = useCallback(() => {
    setVolCalcFailedInfo(null);
  }, []);

  const stepsCurrent = !session
    ? 0
    : isOtherSurface
      ? otherSurfacePreview
        ? 2
        : 1
      : !session.is_validated
        ? 1
        : session.status === "imported"
          ? 3
          : 2;

  return {
    session,
    source,
    setSource,
    loading,
    addingScreenshot,
    validating,
    importing,
    validationErrors,
    validationStatusCode,

    importDestination,
    setImportDestination,
    importDate,
    setImportDate,

    isBusy,
    stepsCurrent,

    volCalcFailedInfo,
    confirmImportSkippingVol,
    dismissVolCalcFailed,

    requestClose,
    handleUpload,
    handleAddScreenshot,
    handleApprove,
    handleImport,
    handleTableUpdate,

    isOtherSurface,
    normalizeRows,
    underlying,
    handleUnderlyingChange,
    notices,
    otherSurfacePreview,
    previewing,
    filling,
    handlePreview,
    dismissOtherSurfacePreview,
    handleFill,
    otherSurfaceProductId,
    otherSurfaceProductStyle,
    otherSurfaceProductLabel: formatProductContractStyleLabel(
      otherSurfaceProductStyle
    ),
    handleOtherSurfaceProductChange,
  };
}
